import * as React from 'react';
import {useState, useCallback, useEffect} from 'react';

import * as serviceWorker from '../serviceWorkerRegistration';
import CloseIcon from '@mui/icons-material/Close';

import {
  Button,
  IconButton,
  Snackbar,
  Switch,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import PushNotificationService from '../services/PushNotificationService';

interface IServiceWorkerUIProps {
  children?: React.ReactNode;
  onUpdate?: () => void;
}

export default function ServiceWorkerUI(props: IServiceWorkerUIProps) {
  const [waitingWorker, set_waitingWorker] = useState<ServiceWorker | null>(null);
  const [newVersionAvailable, set_newVersionAvailable] = useState(false);
  const [openSnackbar, set_openSnackbar] = useState(false);
  const [deferredPrompt, set_deferredPrompt] = useState<any>();
  const [showInstallPromotion, set_showInstallPromotion] = useState<boolean>(false);
  const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState<boolean>(false);
  const [showReload, setShowReload] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermission>('default');
  const [pushSupported, setPushSupported] = useState<boolean>(false);

  const handleShowInstallation = useCallback((e: any) => {
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault();
    // Stash the event so it can be triggered later.
    set_deferredPrompt(e);
    // Update UI notify the user they can install the PWA
    set_showInstallPromotion(true);
  }, []);

  const installationButtonHit = async () => {
    // Hide the app provided install promotion
    set_showInstallPromotion(false);
    // Show the install prompt

    if (deferredPrompt && deferredPrompt.prompt) deferredPrompt.prompt();
    // Wait for the user to respond to the prompt
    const {outcome} = await deferredPrompt.userChoice;
    // Optionally, send analytics event with outcome of user choice
    console.log(`User response to the install prompt: ${outcome}`);
    // We've used the prompt, and can't use it again, throw it away
    set_deferredPrompt(null);
  };

  const handleAppInstalled = useCallback((e: any) => {
    set_showInstallPromotion(false);
    // Clear the deferredPrompt so it can be garbage collected
    set_deferredPrompt(null);
    // Optionally, send analytics event to indicate successful install
    console.log('PWA was installed');
  }, []);

  const checkPushNotificationStatus = async () => {
    try {
      const pushService = PushNotificationService.getInstance();
      const isSubscribed = await pushService.isSubscribed();
      const permission = await pushService.getNotificationPermission();

      setPushNotificationsEnabled(isSubscribed);
      setNotificationPermission(permission);
    } catch (error) {
      console.error('Error checking push notification status:', error);
    }
  };

  const handlePushNotificationToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    setError(null);
    setIsLoading(true);

    try {
      const pushService = PushNotificationService.getInstance();

      if (enabled) {
        // First check if notifications are supported
        if (!pushService.isSupported()) {
          throw new Error('Push-Benachrichtigungen werden in diesem Browser nicht unterstützt');
        }

        // Request notification permission
        const hasPermission = await pushService.requestNotificationPermission();
        if (!hasPermission) {
          throw new Error(
            'Benachrichtigungserlaubnis verweigert. Bitte aktivieren Sie Benachrichtigungen in Ihren Browsereinstellungen.'
          );
        }

        // Subscribe to push notifications
        await pushService.subscribeToPushNotifications();
        setPushNotificationsEnabled(true);
        setNotificationPermission('granted');

        // Show success message
        setError('Push-Benachrichtigungen erfolgreich aktiviert!');
        setTimeout(() => setError(null), 3000);
      } else {
        await pushService.unsubscribeFromPushNotifications();
        setPushNotificationsEnabled(false);

        // Show success message
        setError('Push-Benachrichtigungen erfolgreich deaktiviert!');
        setTimeout(() => setError(null), 3000);
      }
    } catch (error) {
      console.error('Fehler beim Umschalten der Push-Benachrichtigungen:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Fehler beim Umschalten der Push-Benachrichtigungen';
      setError(errorMessage);
      setPushNotificationsEnabled(!enabled); // Revert the toggle
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    window.addEventListener('beforeinstallprompt', handleShowInstallation);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleShowInstallation);
    };
  }, [handleShowInstallation]);

  useEffect(() => {
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [handleAppInstalled]);

  useEffect(() => {
    const initializePushNotifications = async () => {
      if ('serviceWorker' in navigator) {
        try {
          const pushService = PushNotificationService.getInstance();
          setPushSupported(pushService.isSupported());

          // Wait for service worker to be ready before initializing push notifications
          const registration = await navigator.serviceWorker.ready;
          console.log('Service Worker is ready:', registration);

          // Initialize push notification service
          await pushService.initialize();

          // Check current status
          await checkPushNotificationStatus();
        } catch (error) {
          console.error('Fehler beim Initialisieren der Push-Benachrichtigungen:', error);
          setError(
            'Fehler beim Initialisieren der Push-Benachrichtigungen: ' +
              (error instanceof Error ? error.message : String(error))
          );
          setPushSupported(false);
        } finally {
          setIsLoading(false);
        }
      } else {
        console.log('Service Worker not supported');
        setPushSupported(false);
        setIsLoading(false);
      }
    };

    initializePushNotifications();
  }, []);

  const reloadPage = () => {
    waitingWorker?.postMessage({type: 'SKIP_WAITING'});
    setShowReload(false);
    window.location.reload();
  };

  useEffect(() => {
    console.log('newVersionAvailable changed');
    if (newVersionAvailable) {
      console.log('openSnackbar');
      set_openSnackbar(true);
    } //show snackbar with refresh button
  }, [newVersionAvailable]);

  const getNotificationStatusMessage = () => {
    if (!pushSupported) {
      return 'Push-Benachrichtigungen werden in diesem Browser nicht unterstützt';
    }

    switch (notificationPermission) {
      case 'granted':
        return pushNotificationsEnabled
          ? 'Push-Benachrichtigungen sind aktiviert'
          : 'Push-Benachrichtigungen verfügbar';
      case 'denied':
        return 'Benachrichtigungserlaubnis verweigert. Bitte in den Browsereinstellungen aktivieren.';
      case 'default':
        return 'Klicken Sie, um Push-Benachrichtigungen zu aktivieren';
      default:
        return '';
    }
  };

  return (
    <>
      {showInstallPromotion && (
        <Box sx={{p: 2, backgroundColor: 'primary.light', color: 'white', textAlign: 'center'}}>
          <Typography variant='body2' sx={{mb: 1}}>
            Installieren Sie diese App für ein besseres Erlebnis!
          </Typography>
          <Button
            variant='contained'
            color='secondary'
            onClick={installationButtonHit}
            size='small'>
            Als App installieren
          </Button>
        </Box>
      )}

      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={pushNotificationsEnabled}
              onChange={handlePushNotificationToggle}
              disabled={!pushSupported || isLoading || notificationPermission === 'denied'}
            />
          }
          label={isLoading ? 'Wird verarbeitet...' : 'Push-Benachrichtigungen'}
        />

        <Typography variant='caption' display='block' color='textSecondary' sx={{mt: 0.5, mb: 1}}>
          {getNotificationStatusMessage()}
        </Typography>

        {notificationPermission === 'denied' && (
          <Alert severity='warning' sx={{mt: 1}}>
            Benachrichtigungen sind blockiert. Um sie zu aktivieren:
            <br />
            1. Klicken Sie auf das Schloss-Symbol in der Adressleiste Ihres Browsers
            <br />
            2. Erlauben Sie Benachrichtigungen für diese Website
            <br />
            3. Aktualisieren Sie die Seite
          </Alert>
        )}
      </Box>

      {props.children}

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        message={error}
        action={
          <IconButton size='small' color='inherit' onClick={() => setError(null)}>
            <CloseIcon fontSize='small' />
          </IconButton>
        }
      />

      <Snackbar
        key={'updateInfo'}
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => {
          set_openSnackbar(false);
        }}
        TransitionProps={{
          onExited: () => {
            set_openSnackbar(false);
          },
        }}
        message={'Eine neue Version ist verfügbar'}
        action={
          <React.Fragment>
            <Button color='secondary' size='small' onClick={reloadPage}>
              Aktualisieren
            </Button>
            <IconButton
              aria-label='schließen'
              color='inherit'
              sx={{p: 0.5}}
              onClick={() => {
                set_openSnackbar(false);
              }}>
              <CloseIcon />
            </IconButton>
          </React.Fragment>
        }
      />

      <Dialog
        open={showReload}
        onClose={() => setShowReload(false)}
        aria-labelledby='update-dialog-title'
        aria-describedby='update-dialog-description'>
        <DialogTitle id='update-dialog-title'>Neue Version verfügbar</DialogTitle>
        <DialogContent>
          <DialogContentText id='update-dialog-description'>
            Eine neue Version ist verfügbar. Möchten Sie jetzt aktualisieren?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReload(false)} color='primary'>
            Später
          </Button>
          <Button onClick={reloadPage} color='primary' autoFocus>
            Aktualisieren
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
